import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { groupService } from '../../services/group.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import './InputPermissionsGroup.scss'

interface InputPermissionsGroupProps {
  value?: number[]
  onChange?: (value: number[]) => void
  disabled?: boolean
  /** If provided, use these instead of loading (e.g. when parent already loaded groups) */
  groups?: Array<{ id: number; name: string }>
  menuPortalZIndex?: number
}

export const InputPermissionsGroup: React.FC<InputPermissionsGroupProps> = ({
  value = [],
  onChange,
  disabled = false,
  groups: groupsProp,
  menuPortalZIndex = 1060,
}) => {
  const { t } = useTranslation()
  const [groupsState, setGroupsState] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const groups = groupsProp ?? groupsState

  useEffect(() => {
    if (groupsProp != null) return
    const loadGroups = async () => {
      if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Group)) {
        setGroupsState([])
        return
      }
      setLoading(true)
      setLoadError(null)
      try {
        const response = await groupService.listAll()
        setGroupsState(response.results || [])
      } catch (error) {
        setLoadError(t('you_dont_have_permissions_to_do_that'))
      } finally {
        setLoading(false)
      }
    }
    loadGroups().catch(() => {}) // Safety net - prevent unhandled rejection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsProp])

  const handleChange = (selectedOptions: any) => {
    const selectedGroups = selectedOptions ? selectedOptions.map((option: any) => option.value) : []
    onChange?.(selectedGroups)
  }

  const options = (() => {
    const mapped = groups.map((group) => ({ value: group.id, label: group.name }))
    const existingIds = new Set(mapped.map((opt) => opt.value))
    value.forEach((id) => {
      if (!existingIds.has(id)) {
        mapped.push({ value: id, label: `${t('group')} #${id}` })
      }
    })
    return mapped
  })()

  const getValue = () => {
    const selectedSet = new Set(value)
    return options.filter((opt) => selectedSet.has(opt.value))
  }

  const customStyles = {
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: '#6c757d',
      color: '#ffffff',
      borderRadius: '0.375rem',
      padding: '0.25rem 0.5rem',
      margin: '0.125rem',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#ffffff',
      fontSize: '0.875rem',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#ffffff',
      ':hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        color: '#ffffff',
      },
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: menuPortalZIndex,
    }),
  }

  return (
    <div className="docdot-input-select" style={{ opacity: disabled ? 0.6 : 1 }}>
      {loadError && (
        <small className="text-warning d-block mb-1">
          <i className="bi bi-exclamation-triangle me-1" />
          {loadError}
        </small>
      )}
      <div>
        <Select
          isMulti
          options={options}
          value={getValue()}
          onChange={handleChange}
          placeholder={t('select_groups')}
          className="react-select-container"
          classNamePrefix="react-select"
          isClearable={true}
          isSearchable={false}
          openMenuOnClick={true}
          isDisabled={disabled}
          isLoading={loading}
          styles={customStyles}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          filterOption={(option, inputValue) => {
            return option.label.toLowerCase().includes(inputValue.toLowerCase())
          }}
          components={{
            MultiValue: ({ data, removeProps }) => {
              return (
                <div
                  className="tag-wrap btn p-0 d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: '#343a40',
                    color: '#ffffff',
                    borderRadius: '0.375rem',
                    padding: '0.375rem 0.75rem',
                    margin: '0.125rem',
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (removeProps.onClick) {
                        removeProps.onClick(e as any)
                      }
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="btn p-0 d-flex align-items-center"
                    style={{
                      color: '#ffffff',
                      padding: '0',
                      margin: '0 5px',
                      gap: '0.25rem',
                    }}
                    title={t('remove_group')}
                  >
                    <i className="bi bi-x" style={{ fontSize: '1.125rem', display: 'flex', alignItems: 'center' }}></i>
                    <span style={{ fontSize: '0.875rem', padding: '0.125rem 0' }}>{data.label}</span>
                  </button>
                </div>
              )
            },
            ClearIndicator: ({ innerProps }) => (
              <div {...innerProps} className="react-select__clear-indicator" title={t('clear_all')}>
                <i className="bi bi-x" style={{ fontSize: '1rem' }}></i>
              </div>
            ),
          }}
        />
      </div>
    </div>
  )
}
